import { useContext, useEffect, useRef, useState } from 'react';
import { useEventBus } from '../../util/EventBus';
import { useBraze } from '../../vendors/braze/useBraze';
import { AnalyticsContext } from '../AnalyticsContext';
import { PageType, TrackingData } from '../TrackingData';
import { TrackingEvent } from '../TrackingEvent';

/** Observes analytics events and dispatches them to Braze when needed.
 *
 * @requires <BrazeProvider /> and <AnalyticsProvider> as ancestors in the tree. */
export const BrazeAnalyticsDispatcher = () => {
  const { bus } = useContext(AnalyticsContext);

  /** We need a buffer here because we might start receiving events before braze is loaded and initialized.
   * We use a mutable array and a flag rather than replacing the array, because otherwise it's easy for
   * different setter calls to get in a race.
   */
  const brazeBuffer = useRef<BrazeEvent[]>([]);

  const [flushRequired, setFlushRequired] = useState(false);

  const braze = useBraze();

  useEffect(() => {
    /** Flush the braze buffer when possible */
    if (braze && flushRequired) {
      const buffer = brazeBuffer.current;

      while (buffer.length > 0) {
        const event = buffer.shift();
        if (!event) return;
        braze.logCustomEvent(event.name, event.data);
      }

      setFlushRequired(false);

      braze.requestImmediateDataFlush();
    }
  }, [braze, flushRequired]);

  useEventBus(bus, ({ name, data }) => {
    const brazeEvent = translateEventForBraze(name, data);

    if (brazeEvent) {
      brazeBuffer.current.push(brazeEvent);
      setFlushRequired(true);
    }
  });

  return null;
};

interface BrazeEvent {
  name: string;
  data: Record<string, string | boolean | undefined>;
}

const translateEventForBraze = (
  eventName: TrackingEvent,
  data: TrackingData,
): BrazeEvent | null => {
  switch (eventName) {
    case TrackingEvent.SEARCH_PERFORMED: {
      const { searchData } = data;

      return {
        name: 'Search Result View',
        data: {
          'Search Specialty': searchData?.filtersApplied?.specialty,
          'Search State': searchData?.state,
          'Search Postcode': searchData?.postcode,
          'Search Platform': 'Website',
          'Search Suburb': searchData?.suburb,
        },
      };

      break;
    }

    case TrackingEvent.PROFILE_PAGE_LOADED: {
      if (data.pageData?.pageType === PageType.PRACTICE) {
        const { practiceData } = data;

        return {
          name: 'Practice Profile View',
          data: {
            'Practice Id': practiceData?.grpLocId,
            'Practice Specialty': practiceData?.primarySpecialty,
            'Practice State': practiceData?.state,
            'Practice Postcode': practiceData?.postcode,
            'Practice Suburb': practiceData?.suburb,
            'Practice Has Scripts': practiceData?.rescripts,
            'Practice Name': practiceData?.practiceName,
            'Practice Is Verified': practiceData?.isVerified,
            'Paid Profile': practiceData?.paidProfile,
          },
        };
      }

      break;
    }
  }

  return null;
};
